import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import { Loader2, MapPin, Building2, Clock } from "lucide-react";
import CampusBoundary from "./CampusBoundary/CampusBoundary";

interface CampusBoundaryData {
  geometry: {
    type: string;
    coordinates: number[][][];
  };
}

export interface CampusFormData {
  campus_name: string;
  campus_short_name?: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone_number: string;
  time_zone: string;
  is_active: boolean;
  image_url?: string;
  school_id?: string;
  coordinates?: {
    type: string;
    coordinates: number[][][];
  };
  dawn_to_dusk?: number[];
}

interface CampusFormProps {
  initialData?: Partial<CampusFormData>;
  onSubmit: (data: CampusFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  submitLabel?: string;
  title?: string;
  /** Fixed school, when the form is opened from a specific school's card. */
  schoolId?: string;
  /**
   * Schools to choose from, for the standalone "New Campus" entry point where
   * no school is fixed. Without this the form submitted no school_id at all and
   * the backend fell back to `req.user.school_id`, silently creating the campus
   * under the admin's own school.
   */
  schools?: Array<{ _id: string; school_name: string }>;
}

const timeZones = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
];

const convertMinutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}`;
};

const convertTimeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

export function CampusForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel = "Create Campus",
  title = "Create New Campus",
  schoolId,
  schools,
}: CampusFormProps) {
  // Only offer the picker when the caller did not pin a school.
  const needsSchoolChoice = !schoolId && !!schools?.length;
  const [formData, setFormData] = useState<CampusFormData>({
    campus_name: "",
    campus_short_name: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone_number: "",
    time_zone: "America/New_York",
    is_active: true,
    dawn_to_dusk: [360, 1200], // 6 AM to 8 PM
    school_id: schoolId,
    ...initialData,
  });

  const [campusPolygon, setCampusPolygon] = useState<CampusBoundaryData | null>(
    initialData?.coordinates
      ? {
          geometry: {
            type: "Polygon",
            coordinates: initialData.coordinates.coordinates,
          },
        }
      : null
  );

  const [polygonError, setPolygonError] = useState("");
  const [schoolError, setSchoolError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({ ...prev, ...initialData }));
      if (initialData.coordinates) {
        setCampusPolygon({
          geometry: {
            type: "Polygon",
            coordinates: initialData.coordinates.coordinates,
          },
        });
      }
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!campusPolygon) {
      setPolygonError("Please draw a boundary for the campus on the map.");
      return;
    }

    const resolvedSchoolId = schoolId || formData.school_id;

    // A campus with no school gets silently adopted by the caller's own school
    // on the backend, so refuse to submit without one.
    if (!resolvedSchoolId) {
      setSchoolError("Please choose which school this campus belongs to.");
      return;
    }
    setSchoolError(null);

    const submitData: CampusFormData = {
      ...formData,
      school_id: resolvedSchoolId,
      coordinates: {
        type: "Polygon",
        coordinates: campusPolygon.geometry.coordinates,
      },
    };

    onSubmit(submitData);
  };

  const handleInputChange = (
    field: keyof CampusFormData,
    value: string | boolean | number[] | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const dawnTime = formData.dawn_to_dusk
    ? convertMinutesToTime(formData.dawn_to_dusk[0])
    : "06:00";
  const duskTime = formData.dawn_to_dusk
    ? convertMinutesToTime(formData.dawn_to_dusk[1])
    : "20:00";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">{title}</h2>
        <p className="text-sm text-muted-foreground">
          Fill in the campus information and define the campus boundary on the map.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Layout container - 2 columns: form fields and map */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Form Fields Column */}
          <div className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Campus Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {needsSchoolChoice && (
                  <div className="space-y-2">
                    <Label htmlFor="school_id">School *</Label>
                    <Select
                      value={formData.school_id ?? ""}
                      onValueChange={(value) => {
                        handleInputChange("school_id", value);
                        setSchoolError(null);
                      }}
                    >
                      <SelectTrigger id="school_id">
                        <SelectValue placeholder="Choose a school" />
                      </SelectTrigger>
                      <SelectContent>
                        {schools?.map((s) => (
                          <SelectItem key={s._id} value={s._id}>
                            {s.school_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {schoolError && (
                      <Alert variant="destructive">
                        <AlertDescription>{schoolError}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="campus_name">Campus Name *</Label>
                    <Input
                      id="campus_name"
                      value={formData.campus_name}
                      onChange={(e) =>
                        handleInputChange("campus_name", e.target.value)
                      }
                      required
                      placeholder="Main Campus"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="campus_short_name">Short Name</Label>
                    <Input
                      id="campus_short_name"
                      value={formData.campus_short_name}
                      onChange={(e) =>
                        handleInputChange("campus_short_name", e.target.value)
                      }
                      placeholder="MC"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image_url">Campus Image URL</Label>
                  <Input
                    id="image_url"
                    type="url"
                    value={formData.image_url || ""}
                    onChange={(e) =>
                      handleInputChange("image_url", e.target.value)
                    }
                    placeholder="https://example.com/campus-image.jpg"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Location Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Location & Contact
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="address">Address *</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    required
                    placeholder="123 University Ave"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      required
                      placeholder="New York"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) =>
                        handleInputChange("state", e.target.value)
                      }
                      required
                      placeholder="NY"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip">ZIP Code *</Label>
                    <Input
                      id="zip"
                      value={formData.zip}
                      onChange={(e) => handleInputChange("zip", e.target.value)}
                      required
                      placeholder="10001"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone_number">Phone Number *</Label>
                  <Input
                    id="phone_number"
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) =>
                      handleInputChange("phone_number", e.target.value)
                    }
                    required
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Campus Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="time_zone">Time Zone *</Label>
                  <Select
                    value={formData.time_zone}
                    onValueChange={(value) =>
                      handleInputChange("time_zone", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select time zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeZones.map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {tz.replace("_", " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="dawn_time">Dawn Time</Label>
                    <Input
                      id="dawn_time"
                      type="time"
                      value={dawnTime}
                      onChange={(e) => {
                        const minutes = convertTimeToMinutes(e.target.value);
                        handleInputChange("dawn_to_dusk", [
                          minutes,
                          formData.dawn_to_dusk?.[1] || 1200,
                        ]);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dusk_time">Dusk Time</Label>
                    <Input
                      id="dusk_time"
                      type="time"
                      value={duskTime}
                      onChange={(e) => {
                        const minutes = convertTimeToMinutes(e.target.value);
                        handleInputChange("dawn_to_dusk", [
                          formData.dawn_to_dusk?.[0] || 360,
                          minutes,
                        ]);
                      }}
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      handleInputChange("is_active", checked)
                    }
                  />
                  <Label htmlFor="is_active">Campus is active</Label>
                </div>

              </CardContent>
            </Card>
          </div>

          {/* Map Column */}
          <div className="space-y-6">
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Campus Boundary
                </CardTitle>
              </CardHeader>
              <CardContent>
                {polygonError && (
                  <Alert className="mb-4 border-red-500">
                    <AlertDescription className="text-red-600">
                      {polygonError}
                    </AlertDescription>
                  </Alert>
                )}

                <CampusBoundary
                  initialBoundaryData={campusPolygon}
                  onBoundaryChange={(boundary) => {
                    setCampusPolygon(boundary);
                    if (boundary) {
                      setPolygonError("");
                    }
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
